import React, { useMemo } from 'react';
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { buildPartnerDetailsViewModel } from './partnerDetailsView';
import AttendeeEligibilityBadge from '../shared/registration/AttendeeEligibilityBadge';
import RequireJoinApprovalBadge from '../shared/registration/RequireJoinApprovalBadge';

const linkSx = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#1565C0',
  textDecoration: 'underline',
  wordBreak: 'break-all',
  '&:hover': { color: 'info.dark' },
};

const DetailField = ({ label, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontWeight: 700,
        color: 'text.secondary',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const TextValue = ({ value, preserveLineBreaks = false }) => (
  <Typography
    variant="body2"
    sx={{
      color: 'text.primary',
      lineHeight: 1.6,
      fontWeight: 500,
      whiteSpace: preserveLineBreaks ? 'pre-wrap' : 'normal',
    }}
  >
    {value}
  </Typography>
);

const LinkValue = ({ href, label }) => {
  if (!href) {
    return <TextValue value="—" />;
  }

  return (
    <Typography component="a" href={href} target="_blank" rel="noopener noreferrer" sx={linkSx}>
      {label || href}
    </Typography>
  );
};

const PartnerDetailsDialog = ({ open, partner, onClose }) => {
  const model = useMemo(() => buildPartnerDetailsViewModel(partner), [partner]);

  if (!open || !partner) {
    return null;
  }

  const userInitial = (model?.fullName || 'P').charAt(0).toUpperCase();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
          fontWeight: 700,
        }}
      >
        Partner Details
        <IconButton aria-label="Close partner details" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {!model ? (
          <Typography variant="body2" color="text.secondary">
            Partner details are unavailable.
          </Typography>
        ) : (
          <Box>
            {model.coverUrl ? (
              <Box
                component="img"
                src={model.coverUrl}
                alt={`${model.fullName} cover banner`}
                sx={{
                  width: '100%',
                  maxHeight: 180,
                  objectFit: 'cover',
                  borderRadius: 2,
                  mb: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
            ) : null}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar
                src={model.logoUrl || undefined}
                alt={model.fullName}
                sx={{ width: 64, height: 64 }}
              >
                {userInitial}
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {model.fullName}
              </Typography>
            </Box>

            <DetailField label="Display Name">
              <TextValue value={model.fullName} />
            </DetailField>

            <DetailField label="Category">
              <TextValue value={model.category} />
            </DetailField>

            <DetailField label="Short Description">
              <TextValue value={model.shortDescription} preserveLineBreaks />
            </DetailField>

            <DetailField label="About Description">
              <TextValue value={model.description} preserveLineBreaks />
            </DetailField>

            <DetailField label="Website">
              <LinkValue href={model.websiteUrl} />
            </DetailField>

            <DetailField label="Instagram">
              <LinkValue href={model.instagramUrl} />
            </DetailField>

            <DetailField label="Facebook">
              <LinkValue href={model.facebookUrl} />
            </DetailField>

            <DetailField label="WhatsApp Community">
              <LinkValue href={model.whatsappUrl} />
            </DetailField>

            {model.community ? (
              <>
                <Divider sx={{ my: 2.5 }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 2,
                  }}
                >
                  Community
                </Typography>

                <DetailField label="Require Admin Approval">
                  <RequireJoinApprovalBadge required={model.community.requireJoinApproval} />
                </DetailField>

                <DetailField label="Attendee Eligibility">
                  <AttendeeEligibilityBadge
                    value={model.community.attendeeEligibility}
                    variant="chip"
                  />
                </DetailField>
              </>
            ) : null}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PartnerDetailsDialog;
